/**
 * GearX variant dropdown fix + safe option disabling (no PUB_SUB_EVENTS required)
 *
 * Goals:
 *  - On variant-selects <select> change:
 *    1) find matching variant from embedded JSON
 *    2) update ALL id inputs (main + sticky)
 *    3) update URL ?variant=
 *    4) fetch ?variant=ID&section_id=SECTION
 *    5) replace price / availability / sku / ATC state in the DOM
 *  - Additionally:
 *    - Disable impossible options in the OTHER dropdown(s) (do not hide)
 *    - Avoid locking user into one path
 *    - Prevent race-condition overwrites (token)
 */

(function () {
  // ===== SETTINGS =====
  const DISABLE_SOLD_OUT = true;   // disable options that only lead to sold-out variants
  const AUTO_SNAP_INVALID = false; // leave false to avoid "lock-in" behavior

  // Prevent loops when we programmatically adjust selects (only used if AUTO_SNAP_INVALID true)
  let IS_INTERNAL_UPDATE = false;

  // HARDENING: prevent older fetch responses from overwriting newer selection
  let LAST_REQUEST_TOKEN = 0;

  function parseVariants(vs) {
    const json = vs.querySelector('script[type="application/json"]');
    if (!json) return null;
    try {
      return JSON.parse(json.textContent) || null;
    } catch (e) {
      return null;
    }
  }

  function getSelects(vs) {
    const selects = Array.from(vs.querySelectorAll('select[data-option-position], select'));
    selects.sort((a, b) => Number(a.dataset.optionPosition || 0) - Number(b.dataset.optionPosition || 0));
    return selects;
  }

  function getSelectedOptions(vs) {
    return getSelects(vs).map((s) => s.value);
  }

  function findVariant(variants, selectedOptions) {
    if (!Array.isArray(variants)) return null;
    return (
      variants.find((v) => {
        if (!v || !Array.isArray(v.options)) return false;
        for (let i = 0; i < selectedOptions.length; i++) {
          if (v.options[i] !== selectedOptions[i]) return false;
        }
        return true;
      }) || null
    );
  }

  function getRoot(vs) {
    return vs.closest('product-info') || vs.closest('.product--parent') || document;
  }

  function setAllVariantIdInputs(root, variantId) {
    const inputs = [
      ...root.querySelectorAll('form[action^="/cart/add"] input[name="id"]'),
      ...root.querySelectorAll('input.product-variant-id'),
    ];
    const unique = Array.from(new Set(inputs)).filter(Boolean);

    unique.forEach((input) => {
      if (String(input.value) !== String(variantId)) {
        input.value = variantId;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function updateUrlVariant(variantId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    } catch (e) {}
  }

  function parseDoc(html) {
    try {
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      return null;
    }
  }

  function replaceEl(currentEl, newEl) {
    if (!currentEl || !newEl) return;
    currentEl.replaceWith(newEl);
  }

  function syncAddToCartState(root, variant) {
  const btn = root.querySelector('button[name="add"], [type="submit"][name="add"], .product-form__submit');
  if (!btn) return;

  const textSpan = btn.querySelector('.product-form-btn-text') || btn.querySelector('span') || null;

  const textAdd = btn.getAttribute('data-text-add') || 'Add to cart';
  const textPre = btn.getAttribute('data-text-preorder') || 'Pre-order';
  const textSold = btn.getAttribute('data-text-soldout') || 'Sold out';

  const qty = typeof variant?.inventory_quantity === 'number' ? variant.inventory_quantity : 0;
  const isBackorderPreorder =
    !!variant &&
    !!variant.inventory_management &&
    variant.inventory_policy === 'continue' &&
    qty <= 0;

  // Sold out only if unavailable AND not a continue-selling preorder
  if (variant && variant.available === false && !isBackorderPreorder) {
    btn.setAttribute('disabled', 'disabled');
    btn.classList.add('disabled');
    btn.setAttribute('aria-disabled', 'true');

    if (textSpan) textSpan.textContent = textSold;
    return;
  }

  // Pre-order / backorder
  if (isBackorderPreorder) {
    btn.removeAttribute('disabled');
    btn.classList.remove('disabled');
    btn.setAttribute('aria-disabled', 'false');

    if (textSpan) textSpan.textContent = textPre;
    return;
  }

  // Normal add to cart
  btn.removeAttribute('disabled');
  btn.classList.remove('disabled');
  btn.setAttribute('aria-disabled', 'false');

  if (textSpan) textSpan.textContent = textAdd;
}

  function syncVariantUIFromFetchedSection(root, fetchedDoc) {
    // Price blocks: replace by exact id match (GearX uses id="price-template--...__main-...")
    const livePriceEls = root.querySelectorAll('[id^="price-"]');
    livePriceEls.forEach((liveEl) => {
      const id = liveEl.getAttribute('id');
      if (!id) return;
      const newEl = fetchedDoc.getElementById(id);
      if (newEl) replaceEl(liveEl, newEl);
    });

    // Availability
    const liveAvail = root.querySelector('.item-availability-info');
    const newAvail = fetchedDoc.querySelector('.item-availability-info');
    if (liveAvail && newAvail) replaceEl(liveAvail, newAvail);

    // SKU
    const liveSkuRow = root.querySelector('.item-product-code .item-product-code-info, .item-product-code-info');
    const newSkuRow = fetchedDoc.querySelector('.item-product-code .item-product-code-info, .item-product-code-info');
    if (liveSkuRow && newSkuRow) liveSkuRow.textContent = newSkuRow.textContent;

    // Stock countdown
    const liveCountdown = root.querySelector('.product_progress_countdow .primary-color');
    const newCountdown = fetchedDoc.querySelector('.product_progress_countdow .primary-color');
    if (liveCountdown && newCountdown) liveCountdown.textContent = newCountdown.textContent;

    // Preorder / estimated ship date note
    const livePreorderNoteWrap = root.querySelector('.js-preorder-note-wrap');
    const newPreorderNoteWrap = fetchedDoc.querySelector('.js-preorder-note-wrap');

    if (livePreorderNoteWrap) {
      if (newPreorderNoteWrap) {
        replaceEl(livePreorderNoteWrap, newPreorderNoteWrap);
      } else {
        livePreorderNoteWrap.innerHTML = '';
      }
    }
  }

  async function fetchAndSync(vs, root, variant) {
    const sectionId = vs.dataset.section;
    const productUrl = vs.dataset.url;
    if (!sectionId || !productUrl || !variant || !variant.id) return;

    const url = `${productUrl}?variant=${variant.id}&section_id=${encodeURIComponent(sectionId)}`;

    // HARDENING: token for this request (ignores out-of-order responses)
    const token = ++LAST_REQUEST_TOKEN;

    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      const html = await res.text();

      // If a newer request started, ignore this response
      if (token !== LAST_REQUEST_TOKEN) return;

      const doc = parseDoc(html);
      if (!doc) return;

      syncVariantUIFromFetchedSection(root, doc);
      syncAddToCartState(root, variant);
    } catch (e) {}
  }

  // ===== OPTION DISABLING (SAFE) =====
  function variantMatchesOtherSelections(variant, selected, idxToEvaluate) {
    if (!variant || !Array.isArray(variant.options)) return false;

    for (let i = 0; i < selected.length; i++) {
      if (i === idxToEvaluate) continue; // don't constrain the option we're evaluating
      if (!selected[i]) continue;
      if (variant.options[i] !== selected[i]) return false;
    }
    return true;
  }

  function applyDisabling(vs, variants, activeIndex) {
    const selects = getSelects(vs);
    if (!selects.length) return;

    const selected = selects.map((s) => s.value);

    selects.forEach((sel, idx) => {
      // Do NOT disable options in the select the user just changed (prevents lock-in)
      if (activeIndex === idx) return;

      const opts = Array.from(sel.options);

      opts.forEach((opt) => {
        opt.disabled = false;

        const val = opt.value;

        const matches = variants.filter((v) => {
          if (!variantMatchesOtherSelections(v, selected, idx)) return false;
          return v.options && v.options[idx] === val;
        });

        const exists = matches.length > 0;
        const anyAvailable = matches.some((v) => v.available === true);

        if (!exists) {
          opt.disabled = true;
          return;
        }

        if (DISABLE_SOLD_OUT && !anyAvailable) {
          opt.disabled = true;
        }
      });

      if (AUTO_SNAP_INVALID) {
        const current = opts.find((o) => o.value === sel.value);
        if (!current || current.disabled) {
          const firstEnabled = opts.find((o) => !o.disabled);
          if (firstEnabled) {
            IS_INTERNAL_UPDATE = true;
            try {
              sel.value = firstEnabled.value;
            } finally {
              IS_INTERNAL_UPDATE = false;
            }
          }
        }
      }
    });
  }

  // ===== MAIN HANDLER =====
  async function handleSelectChange(selectEl) {
    const vs = selectEl.closest('variant-selects');
    if (!vs) return;

    const variants = parseVariants(vs);
    if (!variants) return;

    const selects = getSelects(vs);
    const activeIndex = selects.indexOf(selectEl);

    // 1) Find exact variant FIRST (prevents "no match → no fetch → price stuck")
    const selectedOptions = getSelectedOptions(vs);
    const matched = findVariant(variants, selectedOptions);

    // 2) Disable options in other dropdown(s) (safe)
    applyDisabling(vs, variants, activeIndex);

    // If no exact match, do not snap or loop; just stop here.
    // (You can enable AUTO_SNAP_INVALID if you want it to auto-pick.)
    if (!matched) return;

    const root = getRoot(vs);

    // 3) Update ids + URL
    setAllVariantIdInputs(root, matched.id);
    updateUrlVariant(matched.id);

    // 4) Fetch + sync UI (price / sku / availability)
    await fetchAndSync(vs, root, matched);

    // 5) Re-apply disabling after fetch (availability state can matter)
    applyDisabling(vs, variants, null);
  }

  // Bubble phase so we don’t interfere with theme listeners
  document.addEventListener('change', function (e) {
    if (IS_INTERNAL_UPDATE) return;

    const t = e.target;
    if (!t || t.tagName !== 'SELECT') return;
    if (!t.closest('variant-selects')) return;

    handleSelectChange(t);
  });

  function initialSync() {
    document.querySelectorAll('variant-selects').forEach((vs) => {
      const variants = parseVariants(vs);
      if (variants) applyDisabling(vs, variants, null);

      const selects = getSelects(vs);
      if (!selects.length) return;

      // Sync price/etc for current selection
      handleSelectChange(selects[selects.length - 1]);
    });
  }

  document.addEventListener('DOMContentLoaded', initialSync);
  document.addEventListener('shopify:section:load', initialSync);
})();
